import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Link,
  ThemeProvider,
  Toolbar,
  createTheme,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import Loader from "../../../shared/components/Loader";
import { FileModel } from "../../../shared/models/models";
import { useNavigate } from "react-router-dom";
import { SubmitHandler, useForm } from "react-hook-form";
import UploadFile from "../../../shared/components/UploadFile";
import ProductBatchResultDialog from "./ProductBatchResultDialog";
import { AdminProductsState } from "../../models/AdminProductsModels";
import { createProductFromFile, setBatchResult, setError } from "../../adminProductsSlice";

const UploadProductsFile = () => {
  const { fileResult, loading, error } = useAppSelector<AdminProductsState>(
    (state: any) => state.adminProducts);
  const dispatch = useAppDispatch();

  const { handleSubmit } = useForm<any>();

  const navigate = useNavigate();

  const [file, setFile] = useState<FileModel | null>(null);

  const handlerCreateFile = (file: FileModel | null) => {
    setFile(file);
  };

  const onSubmit: SubmitHandler<any> = () => {
    handleUploadFile();
  };

  const handleUploadFile = async () => {
    if (!file) {
      return;
    }

    dispatch(createProductFromFile(file));
  };

  const handleCancel = () => {
    navigate("/admin/catalog");
  };

  const handleCloseUploadResult = () => {
    dispatch(setBatchResult(undefined));

    if (error) {
      dispatch(setError(undefined));
      return;
    }

    navigate("/admin/catalog");
  };

  const init = async () => {
  };

  useEffect(() => {
    init();
  }, []);

  const csvColumns = [
    "Code",
    "Number",
    "Description"
  ];

  const theme = createTheme({
    components: {
      MuiToolbar: {
        styleOverrides: {
          root: {
            background: "#fff",
            borderBottom: "1px solid #d5d6d9",
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            borderRadius: "6px 6px 6px 6px !important",
          },
        },
      },
    },
  });

  if (loading) {
    return <Loader />;
  }

  return (
    <ThemeProvider theme={theme}>
      <Toolbar className="flex items-center justify-between w-full">
        <div className="flex items-center">
          <p className="heading-2">Excel file upload</p>
        </div>

        <div className="flex">
        </div>
      </Toolbar>

      <Box className="grid grid-cols-4 gap-5 p-5">
        <Box className="col-span-2">
          <form id="uploadFileForm" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <UploadFile file={file} variant="default" onChangeFile={handlerCreateFile} />
            </div>

            <div className="mt-4 flex justify-center">
              <Link
                href="/assets/catalog.csv"
                target="_blank"
                sx={{ textDecoration: "none", fontSize: "14px", fontWeight: "medium", lineHeight: "28px" }}>
                Download template in CSV
              </Link>
            </div>

            <div className="mt-6">
              <p className="text-14-regular" style={{ color: "#000" }}>
                The easiest way to create a CSV file for a batch upload is to put your data
                into a spreadsheet and then export it to a .csv file.
              </p>
            </div>

            <div className="mt-4 text-14-regular" style={{ color: "#414C58" }}>
              <p>
                You don’t need headers, just put in data about your product data in the given order (columns left to right):
              </p>
              <ul style={{ listStyleType: "disc", listStylePosition: "inside" }}>
                {csvColumns.map((column, index) => (<li key={`csv-column-${index}`}>{column}</li>))}
              </ul>
            </div>
          </form>
        </Box>

        <Box>
          <Button form="uploadFileForm" type="submit" variant="contained" className="w-full" disabled={!file}>
            Upload
          </Button>

          <Box className="mt-3">
            <Button onClick={handleCancel} variant="outlined" className="w-full mt-3">
              Cancel
            </Button>
          </Box>
        </Box>
      </Box>

      <ProductBatchResultDialog
        key={`products-dialog-${!!fileResult}`}
        open={!!(fileResult || error)}
        onClose={handleCloseUploadResult}
        error={error}
        fileResult={fileResult}
        loading={loading}
      />
    </ThemeProvider>
  );
};
export default UploadProductsFile;
